import { useState } from "react";
import { api, apiError } from "@/lib/api";
import { useAtlas } from "@/context/AtlasContext";
import { useIndustries, invalidateIndustries } from "@/hooks/useIndustries";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tags, Plus, Lock, Eye, EyeOff, Pencil } from "lucide-react";

export default function Industries() {
  const { has } = useAtlas();
  const items = useIndustries();
  const canManage = has("company.manage");
  const [label, setLabel] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLabel, setEditLabel] = useState("");

  const stock = items.filter((i) => !i.tenant_id);
  // custom rows need to include inactive ones for admins, so fetch all via a separate call
  const [customAll, setCustomAll] = useState(null);
  const refresh = async () => {
    await invalidateIndustries();
    try { const { data } = await api.get("/industries"); setCustomAll(data.filter((i) => i.tenant_id)); } catch {}
  };

  const add = async () => {
    if (!label.trim()) return;
    setSaving(true);
    try { await api.post("/industries", { label: label.trim() }); setLabel(""); toast.success("Industry added"); await refresh(); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  const rename = async (id) => {
    try { await api.put(`/industries/${id}`, { label: editLabel.trim() }); setEditing(null); toast.success("Renamed"); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggle = async (row) => {
    try { await api.put(`/industries/${row.id}`, { active: !row.active }); toast.success(row.active ? "Deactivated" : "Reactivated"); await refresh(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const custom = customAll !== null ? customAll : items.filter((i) => i.tenant_id);

  return (
    <div className="max-w-2xl" data-testid="industries-admin">
      <PageHeader title="Industries" subtitle="Shared industry list used across organizations. Stock entries are read-only." />

      {canManage && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 mb-6 flex items-end gap-2">
          <div className="flex-1"><label className="text-xs font-medium text-slate-600">Add a custom industry</label>
            <Input value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="e.g. Marine Services" className="mt-1" data-testid="industry-add-input" /></div>
          <Button onClick={add} disabled={saving || !label.trim()} data-testid="industry-add-btn"><Plus className="w-4 h-4 mr-1" /> Add</Button>
        </div>
      )}

      {custom.length > 0 && (
        <div className="mb-6">
          <h3 className="text-sm font-semibold text-slate-700 mb-2">Your custom industries</h3>
          <div className="bg-white border border-slate-200 rounded-xl divide-y">
            {custom.map((r) => (
              <div key={r.id} className={`p-3 flex items-center gap-2 text-sm ${r.active ? "" : "opacity-50"}`} data-testid={`industry-custom-${r.key}`}>
                {editing === r.id ? (
                  <><Input value={editLabel} onChange={(e) => setEditLabel(e.target.value)} className="h-8" data-testid="industry-edit-input" />
                    <Button size="sm" onClick={() => rename(r.id)}>Save</Button><Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button></>
                ) : (
                  <><span className="flex-1 text-slate-800">{r.label}</span>
                    <span className="text-[10px] text-slate-400 font-mono">{r.key}</span>
                    {canManage && <button onClick={() => { setEditing(r.id); setEditLabel(r.label); }} className="text-slate-400 hover:text-slate-700" data-testid={`industry-rename-${r.key}`}><Pencil className="w-4 h-4" /></button>}
                    {canManage && <button onClick={() => toggle(r)} className="text-slate-400 hover:text-slate-700" data-testid={`industry-toggle-${r.key}`}>{r.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>}</>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-1.5"><Lock className="w-3.5 h-3.5" /> Stock industries (read-only)</h3>
      <div className="bg-white border border-slate-200 rounded-xl divide-y">
        {stock.map((r) => (
          <div key={r.id} className="p-2.5 flex items-center gap-2 text-sm" data-testid={`industry-stock-${r.key}`}>
            <Tags className="w-3.5 h-3.5 text-slate-300" /><span className="flex-1 text-slate-700">{r.label}</span>
            <span className="text-[10px] text-slate-400 font-mono">{r.key}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
